import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { BioAbsorptionMatrix } from "@/components/BioAbsorptionMatrix";
import { Reveal } from "@/components/Reveal";
import { CmsRegion } from "@/components/cms/CmsRegion";

const ORIGIN_BEATS: { step: string; title: string; body: string }[] = [
  {
    step: "01",
    title: "The Problem",
    body: "Most supplements are built to sell, not to serve—generic ingredients, weak formulations, confusing labels and exaggerated promises. Consumers are left guessing what works and what deserves their trust.",
  },
  {
    step: "02 ",
    title: "The Belief",
    body: "The Label should tell the whole story, and one simple Question Can i take the Supplement that we want to sell.\n\nEvery ingredient, form and dose should have a clear purpose—because supplementation should be precise, understandable and worth taking consistently.",
  },
  {
    step: "03",
    title: "The Commitment",
    body: "TruWe creates precision-formulated nutrition for real needs. We choose thoughtful nutrient forms, meaningful combinations and transparent communication—without miracle claims or unnecessary complexity. If a product cannot genuinely improve your daily wellness routine, we will not launch it.",
  },
];

export const Route = createFileRoute("/about")({
  component: AboutPage,
  head: () => ({
    meta: [
      { title: "About TruWe. Indian Biotech Wellness Brand" },
      {
        name: "description",
        content:
          "Indian biotech wellness brand making research-backed nutraceuticals. FSSAI licensed, GMP-certified, USFDA-registered facility, lab tested.",
      },
      { property: "og:title", content: "About TruWe. Indian Biotech Wellness Brand" },
      {
        property: "og:description",
        content:
          "How TruWe builds research-backed formulas for sleep, hormones, gut and recovery, engineered for the long game.",
      },
      { property: "og:url", content: "https://truwe.in/about" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About TruWe",
          url: "https://truwe.in/about",
          mainEntity: { "@id": "https://truwe.in/#organization" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://truwe.in/" },
            { "@type": "ListItem", position: 2, name: "About", item: "https://truwe.in/about" },
          ],
        }),
      },
    ],
  }),
});

function AboutPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 md:px-8 md:py-20">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">About TruWe</p>
      <h1 className="mt-2 font-display text-4xl font-bold text-brand-deep md:text-5xl">
        The TruWe Story: Science-Backed Wellness
      </h1>
      <div className="mt-6 space-y-5 text-lg text-muted-foreground">
        <p>
          TruWe creates science-backed wellness products designed for performance, balance,
          recovery, and longevity.
        </p>
        <p>
          Clinical precision. Functional ingredients. Modern wellness.
        </p>
      </div>

      {/* Origin story */}
      <section aria-labelledby="origin-story" className="mt-16 md:mt-20">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
          Our origin
        </p>
        <h2
          id="origin-story"
          className="mt-2 font-display text-3xl font-bold text-brand-deep md:text-4xl"
        >
          Built on a simple belief.
        </h2>
        <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
          Truwe was founded on one simple belief. Built for the long game.
        </p>

        <div className="mt-10 grid gap-5 md:grid-cols-3 md:gap-6">
          {ORIGIN_BEATS.map((beat, i) => (
            <Reveal key={beat.step} delay={i * 100}>
              <article className="h-full rounded-2xl border border-border bg-card p-6">
                <p className="font-display text-sm font-semibold tracking-[0.18em] text-brand">
                  {beat.step}
                </p>
                <h3 className="mt-3 font-display text-lg font-semibold text-brand-deep">
                  {beat.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {beat.body}
                </p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={300}>
          <blockquote className="mt-12 border-l-2 border-brand pl-5 font-display text-xl font-medium leading-snug text-brand-deep md:text-2xl">
             {`"Results you can feel. Science you can trust." TruWe — your way to everyday wellness.`}
          </blockquote>
        </Reveal>

        <Reveal delay={350}>
          <div className="mt-12 flex items-start gap-4 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground md:p-6">
            <img
              src="/__l5e/assets-v1/abb35464-bc7d-4811-8701-de130c801d80/fssai-logo-mono.png"
              alt="FSSAI Regulatory Badge"
              width={56}
              height={56}
              loading="lazy"
              className="h-14 w-14 flex-shrink-0 opacity-80 grayscale"
            />
            <div>
              <p className="font-display text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                Regulatory
              </p>
              <p className="mt-2 text-brand-deep">
                FSSAI Licence No.{" "}
                <span className="font-semibold tracking-wide">22625072001267</span>
              </p>
              <p className="mt-1 text-xs">
                Manufactured in an FSSAI-licensed, GMP-certified, USFDA-registered facility · Third-party lab tested on every batch.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Performance", "Formulas tuned to how you train, work and live."],
          ["Balance", "Hormones, gut, mood, the whole system in sync."],
          ["Recovery", "Deep rest and repair at the cellular level."],
          ["Longevity", "Built for the long game, not the trend."],
        ].map(([t, d]) => (
          <div key={t} className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-display text-base font-semibold text-brand-deep">{t}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{d}</p>
          </div>
        ))}
      </div>
      <Link to="/shop">
        <Button size="lg" className="mt-10 rounded-full bg-brand px-7 hover:bg-brand/90">
          Shop the range
        </Button>
      </Link>

      <CmsRegion id="about-extra" label="About — extra sections" />

      <BioAbsorptionMatrix variant="header" className="mt-16" />
    </div>
  );
}
